// Location registry.
//
// Zones are named by IANA identifier only. Abbreviations (KST, PST, PDT, CET,
// CEST) are ambiguous and are never used, and no fixed UTC offset is stored
// anywhere: the offsets move, on dates that do not line up.
//
//   Korea gap    7h or 8h, flipping when Denmark changes (Seoul never does)
//   Seattle gap  usually 9h, but 8h for ~3 weeks each spring and ~1 week each
//                autumn, while the US and EU changeover dates are misaligned
//
// Anything that hardcodes +7, +8 or +9 is wrong twice a year.

/** Where Jonathan is. Display only — he never picks. */
export const HOME_ZONE = 'Europe/Copenhagen';

/** @typedef {'korea' | 'seattle'} LocationId */

/** The two places she picks from. Order is the order they are offered. */
export const LOCATIONS = Object.freeze([
  Object.freeze({ id: 'korea', label: 'Korea', zone: 'Asia/Seoul' }),
  Object.freeze({ id: 'seattle', label: 'Seattle', zone: 'America/Los_Angeles' }),
]);

/**
 * @param {string} id
 * @returns {{found: true, location: {id: LocationId, label: string, zone: string}} | {found: false, id: string}}
 */
export function findLocation(id) {
  const location = LOCATIONS.find((candidate) => candidate.id === id);
  return location ? { found: true, location } : { found: false, id: String(id) };
}

/**
 * @param {string} id
 * @returns {string} the IANA zone for a location id
 * @throws if the id is not a known location
 */
export function zoneForLocation(id) {
  const result = findLocation(id);
  if (!result.found) throw new Error(`unknown location: ${result.id}`);
  return result.location.zone;
}

/**
 * The location is written into the Discord event's description when it is
 * created, and read back out when the page asks what is already booked.
 *
 * The server holds the instant but not which city she was in, and Discord has
 * nowhere else to put it — so the event carries it. Discord stays the record,
 * which is the same reason there is no database anywhere else here.
 */
const LOCATION_MARK = 'where:';

/** @param {string} locationId */
export function describeLocation(locationId) {
  return `${LOCATION_MARK}${locationId}`;
}

/**
 * @param {string} description
 * @returns {string | null} the location id, or null for an event booked before
 *   this was recorded — the caller must cope with not knowing.
 */
export function readLocationFromDescription(description) {
  if (typeof description !== 'string') return null;
  const match = new RegExp(`${LOCATION_MARK}([a-z-]+)`).exec(description);
  if (!match) return null;
  return findLocation(match[1]).found ? match[1] : null;
}
